import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import {
  ALL_FILE_EXTENSIONS,
  ALL_FILENAME_IDENTIFIERS,
  DEFAULT_FILENAME,
  FileExtension,
  FilenameIdentifier,
  ProductDataFilenameConventionFilename,
  fileExtensionLabel,
  filenameIdentifierLabel,
} from 'src/app/models/product-data-filename-conventions/product-data-filename-convention';
import { buildFormatConvention } from 'src/app/utils/format-convention';

// Shared form for the identifier + extensions selection.
// Hosted by CreateDialog (Phase 7) and UpdateDialog (Phase 8).
// No Reason UI here — Reason lives on the Update dialog's own template.
@Component({
  selector: 'app-product-data-filename-convention-form',
  templateUrl: './product-data-filename-convention-form.component.html',
  styleUrls: ['./product-data-filename-convention-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProductDataFilenameConventionFormComponent implements OnInit, OnChanges {
  @Input() initialValue: ProductDataFilenameConventionFilename | null = null;

  @Output() readonly formValue = new EventEmitter<ProductDataFilenameConventionFilename>();
  @Output() readonly formValid = new EventEmitter<boolean>();

  readonly identifiers: readonly FilenameIdentifier[] = ALL_FILENAME_IDENTIFIERS;
  readonly extensions: readonly FileExtension[] = ALL_FILE_EXTENSIONS;
  readonly identifierLabel = filenameIdentifierLabel;
  readonly extensionLabel = fileExtensionLabel;

  identifier: FilenameIdentifier | null = null;
  selectedExtensions: FileExtension[] = [];

  ngOnInit(): void {
    this.hydrate(this.initialValue);
    this.emit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('initialValue' in changes && !changes['initialValue'].firstChange) {
      this.hydrate(this.initialValue);
      this.emit();
    }
  }

  onIdentifierChange(value: FilenameIdentifier | null): void {
    this.identifier = value;
    this.emit();
  }

  onExtensionsChange(value: FileExtension[]): void {
    this.selectedExtensions = value ?? [];
    this.emit();
  }

  formatConvention(extension: FileExtension): string | null {
    if (this.identifier === null) return null;
    return buildFormatConvention(this.identifier, extension);
  }

  private hydrate(value: ProductDataFilenameConventionFilename | null): void {
    if (value === null) {
      this.identifier = null;
      this.selectedExtensions = [];
      return;
    }
    this.identifier = value.identifier;
    this.selectedExtensions = [...value.extensions];
  }

  private emit(): void {
    const valid = this.identifier !== null && this.selectedExtensions.length > 0;
    this.formValid.emit(valid);
    if (valid) {
      this.formValue.emit({
        identifier: this.identifier as FilenameIdentifier,
        extensions: [...this.selectedExtensions],
      });
    }
  }
}

// Re-export for callers that want to seed the form with the default when
// nothing has been picked yet (used by the Create dialog).
export { DEFAULT_FILENAME };
